import { useState, useCallback, useEffect } from 'react';
import { useLocation } from "wouter";
import { useDropzone } from 'react-dropzone';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { useToast } from '@/hooks/use-toast';
import { Upload, Image, Loader2, CheckCircle, AlertCircle, Camera, FileText, Users, Tag, Settings, Zap, X, Plus } from 'lucide-react';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3000';

interface Event {
  id: string;
  name: string;
  date: string;
  location: string;
}

interface ImageMetadata {
  originalName: string;
  technicalData: {
    camera?: string;
    resolution?: string;
    quality?: string;
    iso?: string;
    aperture?: string;
    shutterSpeed?: string;
    focalLength?: string;
    dateTime?: string;
    gps?: string;
  };
  fileInfo: {
    size: number;
    type: string;
    lastModified: number;
  };
  suggestedAttributes: {
    sport?: string;
    timeOfDay?: string;
    weather?: string;
    location?: string;
  };
  bibNumber?: string;
  processingNotes: string[];
}

interface UploadFile extends File {
  id: string;
  preview?: string;
  metadata?: ImageMetadata;
  processed?: boolean;
  processing?: boolean;
}

export default function PhotoSelector() {
  const [files, setFiles] = useState<UploadFile[]>([]);
  const [selectedEventId, setSelectedEventId] = useState<string>('');
  const [category, setCategory] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [events, setEvents] = useState<Event[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processedCount, setProcessedCount] = useState(0);
  const [isLoadingEvents, setIsLoadingEvents] = useState(true);
  const [, navigate] = useLocation();
  const { toast } = useToast();

  // Fetch available events
  useEffect(() => {
    const fetchEvents = async () => {
      try {
        setIsLoadingEvents(true);
        const response = await fetch(`${API_BASE}/api/events`);
        if (response.ok) {
          const eventsData = await response.json();
          // Ensure eventsData is always an array
          setEvents(Array.isArray(eventsData) ? eventsData : []);
        } else {
          console.error('Failed to fetch events:', response.statusText);
          setEvents([]);
        }
      } catch (error) {
        console.error('Error fetching events:', error);
        setEvents([]);
        toast({
          title: "Erro ao carregar eventos",
          description: "Não foi possível carregar a lista de eventos. Tente novamente.",
          variant: "destructive",
        });
      } finally {
        setIsLoadingEvents(false);
      }
    };

    fetchEvents();
  }, [toast]);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const newFiles = acceptedFiles.map((file) => {
      const uploadFile = file as UploadFile;
      uploadFile.id = Math.random().toString(36).substr(2, 9);
      uploadFile.preview = URL.createObjectURL(file);
      return uploadFile;
    });

    setFiles(prev => [...prev, ...newFiles]);
    toast({
      title: "Arquivos adicionados",
      description: `${acceptedFiles.length} arquivo(s) selecionado(s) com sucesso.`,
    });
  }, [toast]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.tiff', '.raw']
    },
    multiple: true
  });

  const removeFile = (fileId: string) => {
    setFiles(prev => {
      const fileToRemove = prev.find(f => f.id === fileId);
      if (fileToRemove?.preview) {
        URL.revokeObjectURL(fileToRemove.preview);
      }
      return prev.filter(f => f.id !== fileId);
    });
  };

  const clearAllFiles = () => {
    files.forEach(file => {
      if (file.preview) {
        URL.revokeObjectURL(file.preview);
      }
    });
    setFiles([]);
    setProcessedCount(0);
  };

  // Extract bib number from filename
  const extractBibNumber = (filename: string): string | undefined => {
    const patterns = [
      /(\d{1,4})(?=\D|$)/,
      /bib[_-]?(\d+)/i,
      /num[_-]?(\d+)/i,
      /(\d+)[_-]?bib/i
    ];

    for (const pattern of patterns) {
      const match = filename.match(pattern);
      if (match) {
        const number = parseInt(match[1]);
        if (number > 0 && number <= 9999) {
          return number.toString();
        }
      }
    }
    return undefined;
  };

  // Mock image processing function
  const processImage = async (file: UploadFile): Promise<ImageMetadata> => {
    // Simulate processing delay
    await new Promise(resolve => setTimeout(resolve, 1000 + Math.random() * 2000));

    const bibNumber = extractBibNumber(file.name);
    
    return {
      originalName: file.name,
      technicalData: {
        camera: "Canon EOS R5",
        resolution: "8192x5464",
        quality: "RAW+JPEG",
        iso: "400",
        aperture: "f/2.8",
        shutterSpeed: "1/500s",
        focalLength: "85mm",
        dateTime: new Date().toISOString(),
        gps: "40.7128,-74.0060"
      },
      fileInfo: {
        size: file.size,
        type: file.type,
        lastModified: file.lastModified
      },
      suggestedAttributes: {
        sport: "Running",
        timeOfDay: "Morning",
        weather: "Sunny",
        location: "Central Park"
      },
      bibNumber,
      processingNotes: [
        "Image quality: Excellent",
        bibNumber ? `Bib number detected: ${bibNumber}` : "No bib number detected",
        "Face detection: 1 person detected",
        "Motion blur: None detected"
      ]
    };
  };

  const processAllImages = async () => {
    if (!selectedEventId) {
      toast({
        title: "Evento não selecionado",
        description: "Por favor, selecione um evento antes de processar as imagens.",
        variant: "destructive",
      });
      return;
    }

    setIsProcessing(true);
    setProcessedCount(0);

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      
      // Mark file as processing
      setFiles(prev => prev.map(f => 
        f.id === file.id ? { ...f, processing: true } : f
      ));

      try {
        const metadata = await processImage(file);
        
        // Mark file as processed
        setFiles(prev => prev.map(f => 
          f.id === file.id ? { 
            ...f, 
            processing: false, 
            processed: true, 
            metadata 
          } : f
        ));

        setProcessedCount(i + 1);
      } catch (error) {
        console.error('Error processing file:', error);
        
        // Mark file as failed
        setFiles(prev => prev.map(f => 
          f.id === file.id ? { ...f, processing: false } : f
        ));

        toast({
          title: "Erro no processamento",
          description: `Falha ao processar ${file.name}`,
          variant: "destructive",
        });
      }
    }

    setIsProcessing(false);
    
    toast({
      title: "Processamento concluído",
      description: `${processedCount} imagem(ns) processada(s) com sucesso.`,
    });
  };

  const goToPhotoViewer = () => {
    if (files.length === 0) {
      toast({
        title: "Nenhuma imagem",
        description: "Adicione pelo menos uma imagem antes de continuar.",
        variant: "destructive",
      });
      return;
    }

    // Store files in sessionStorage to pass to photo viewer
    sessionStorage.setItem('photoViewerFiles', JSON.stringify(files));
    sessionStorage.setItem('photoViewerEvent', selectedEventId);
    sessionStorage.setItem('photoViewerCategory', category);
    sessionStorage.setItem('photoViewerDescription', description);
    
    navigate('/photo-viewer');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div className="text-center space-y-2">
          <h1 className="text-4xl font-bold text-gray-900">Photo Selector</h1>
          <p className="text-lg text-gray-600">
            Selecione múltiplas fotos para processamento em lote e extração de metadados
          </p>
        </div>

        {/* Upload Area */}
        <Card className="border-2 border-dashed border-gray-300 hover:border-blue-400 transition-colors">
          <CardContent className="p-8">
            <div
              {...getRootProps()}
              className={`text-center cursor-pointer transition-all duration-200 ${
                isDragActive ? 'scale-105 bg-blue-50' : ''
              }`}
            >
              <input {...getInputProps()} />
              <div className="space-y-4">
                <div className="mx-auto w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
                  <Upload className="h-8 w-8 text-blue-600" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    Upload Event Photos
                  </h3>
                  <p className="text-gray-600 mb-4">
                    Arraste e solte múltiplas fotos aqui ou clique para navegar
                  </p>
                  <p className="text-sm text-gray-500">
                    Suporta PNG, JPG, TIFF, formatos • Múltiplos arquivos permitidos
                  </p>
                </div>
                <Button variant="outline" className="mt-4">
                  <Plus className="h-4 w-4 mr-2" />
                  Select Photos
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Configuration and Files */}
        {files.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="h-5 w-5" />
                Selected Files ({files.length})
              </CardTitle>
              <CardDescription>
                Configure event details and process your photos
              </CardDescription>
              <Button 
                onClick={clearAllFiles}
                variant="outline" 
                size="sm"
                className="w-fit"
              >
                Clear All
              </Button>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Event Configuration */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="event-select" className="flex items-center gap-2">
                    <Camera className="h-4 w-4" />
                    Event
                  </Label>
                  {isLoadingEvents ? (
                    <div className="flex items-center gap-2 p-2 border rounded">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span className="text-sm text-gray-500">Loading events...</span>
                    </div>
                  ) : (
                    <Select value={selectedEventId} onValueChange={setSelectedEventId}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select an event" />
                      </SelectTrigger>
                      <SelectContent>
                        {events.length > 0 ? (
                          events.map((event) => (
                            <SelectItem key={event.id} value={event.id}>
                              {event.name} - {new Date(event.date).toLocaleDateString()}
                            </SelectItem>
                          ))
                        ) : (
                          <SelectItem value="no-events" disabled>
                            No events available
                          </SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="category" className="flex items-center gap-2">
                    <Tag className="h-4 w-4" />
                    Category
                  </Label>
                  <Input
                    id="category"
                    placeholder="e.g., Race Photos, Finish Line, Awards"
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="description" className="flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  Description
                </Label>
                <Textarea
                  id="description"
                  placeholder="Add any additional notes about this photo batch..."
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={3}
                />
              </div>

              <Separator />

              {/* File Grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                {files.map((file) => (
                  <div key={file.id} className="relative group">
                    <div className="aspect-square bg-gray-100 rounded-lg overflow-hidden border-2 border-gray-200 hover:border-blue-400 transition-colors">
                      {file.preview && (
                        <img
                          src={file.preview}
                          alt={file.name}
                          className="w-full h-full object-cover"
                        />
                      )}
                      <button
                        onClick={() => removeFile(file.id)}
                        className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                    <div className="mt-2 text-center">
                      <p className="text-xs text-gray-600 truncate" title={file.name}>
                        {file.name}
                      </p>
                      <p className="text-xs text-gray-500">
                        {(file.size / 1024 / 1024).toFixed(1)} MB
                      </p>
                      {file.processing && (
                        <Badge variant="secondary" className="mt-1">
                          <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                          Processing
                        </Badge>
                      )}
                      {file.processed && (
                        <Badge variant="default" className="mt-1">
                          <CheckCircle className="h-3 w-3 mr-1" />
                          Processed
                        </Badge>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <Separator className="my-4" />

              {/* Processing Controls */}
              <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
                <Button
                  onClick={processAllImages}
                  disabled={isProcessing || files.length === 0 || !selectedEventId}
                  className="w-full sm:w-auto"
                >
                  {isProcessing ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Processing {processedCount}/{files.length}
                    </>
                  ) : (
                    <>
                      <Zap className="h-4 w-4 mr-2" />
                      Process {files.length} Photos
                    </>
                  )}
                </Button>

                <Button
                  onClick={goToPhotoViewer}
                  disabled={files.length === 0}
                  variant="outline"
                  className="w-full sm:w-auto"
                >
                  <Camera className="h-4 w-4 mr-2" />
                  Continue to Photo Viewer
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}